import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  TrendingUp, Filter, RefreshCw, X as XIcon, Globe, Users, Building2,
  User, Tag, Calendar, BarChart3, Activity, FileText, Search, AlertCircle
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area, LabelList
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import './TendenciaMonitoreo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS_CHART = ['#1565C0', '#E53935', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1', '#D81B60', '#5E35B1', '#F4511E', '#00897B'];
const RED_COLORS = { X: '#000000', Facebook: '#1877F2', Instagram: '#E4405F', Web: '#43A047', YouTube: '#FF0000', TikTok: '#010101', Twitter: '#1DA1F2' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip-tm">
      <p className="label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="value" style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const TendenciaMonitoreo = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filtros, setFiltros] = useState({
    institucion: '',
    nomenclatura: '',
    fecha_desde: '',
    fecha_hasta: '',
    asesor_cnc: ''
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v) queryParams.append(k, v);
      });
      const url = `${API}/tendencia-monitoreo/datos${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error al cargar datos de tendencia:', error);
      toast.error('Error al cargar los datos de tendencia');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const aplicarFiltros = () => {
    setFiltrosAplicados(true);
    fetchData(filtros);
  };

  const limpiarFiltros = () => {
    const limpio = { institucion: '', nomenclatura: '', fecha_desde: '', fecha_hasta: '', asesor_cnc: '' };
    setFiltros(limpio);
    setFiltrosAplicados(false);
    fetchData();
  };

  const resumen = data?.resumen || {};
  const filtrosDisp = data?.filtros_disponibles || {};

  // Timeline por red social para line chart
  const networkTimeline = useMemo(() => {
    if (!data?.timeline) return [];
    const redes = new Set();
    data.timeline.forEach(t => {
      Object.keys(t.redes || {}).forEach(r => redes.add(r));
    });
    return data.timeline.map(t => {
      const item = { fecha: t.fecha, total: t.cantidad };
      redes.forEach(r => { item[r] = (t.redes || {})[r] || 0; });
      return item;
    });
  }, [data?.timeline]);

  const redesList = useMemo(() => {
    if (!networkTimeline.length) return [];
    return Object.keys(networkTimeline[0]).filter(k => k !== 'fecha' && k !== 'total');
  }, [networkTimeline]);

  if (loading && !data) {
    return (
      <div className="tendencia-container">
        <div className="tendencia-loading" data-testid="tendencia-loading">
          <div className="spinner"></div>
          <p>Cargando datos de tendencia...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tendencia-container" data-testid="tendencia-monitoreo">
      <div className="tendencia-header">
        <h1><TrendingUp size={28} /> Tendencia Monitoreo de Medios</h1>
        <p>Tendencias de casos del modulo Monitoreo de Medios: red social, autor, tematica, personas, instituciones</p>
      </div>

      {/* Filtros */}
      <div className="tendencia-filtros" data-testid="tendencia-filtros">
        <div className="filtro-grupo">
          <label>Institucion</label>
          <select data-testid="filtro-institucion" value={filtros.institucion} onChange={e => setFiltros(p => ({ ...p, institucion: e.target.value }))}>
            <option value="">Todas</option>
            {(filtrosDisp.instituciones || []).map(i => (<option key={i} value={i}>{i}</option>))}
          </select>
        </div>
        <div className="filtro-grupo">
          <label>No. Gestion</label>
          <input data-testid="filtro-nomenclatura" type="text" placeholder="Ej: MM-001" value={filtros.nomenclatura} onChange={e => setFiltros(p => ({ ...p, nomenclatura: e.target.value }))} />
        </div>
        <div className="filtro-grupo">
          <label>Fecha desde</label>
          <input data-testid="filtro-fecha-desde" type="date" value={filtros.fecha_desde} onChange={e => setFiltros(p => ({ ...p, fecha_desde: e.target.value }))} />
        </div>
        <div className="filtro-grupo">
          <label>Fecha hasta</label>
          <input data-testid="filtro-fecha-hasta" type="date" value={filtros.fecha_hasta} onChange={e => setFiltros(p => ({ ...p, fecha_hasta: e.target.value }))} />
        </div>
        <div className="filtro-grupo">
          <label>Asesor CNC</label>
          <select data-testid="filtro-asesor" value={filtros.asesor_cnc} onChange={e => setFiltros(p => ({ ...p, asesor_cnc: e.target.value }))}>
            <option value="">Todos</option>
            {(filtrosDisp.asesores_cnc || []).map(a => (<option key={a} value={a}>{a}</option>))}
          </select>
        </div>
        <div className="filtro-acciones">
          <button className="btn-aplicar" onClick={aplicarFiltros} data-testid="btn-aplicar-filtros">
            <Filter size={14} /> Filtrar
          </button>
          {filtrosAplicados && (
            <button className="btn-limpiar" onClick={limpiarFiltros} data-testid="btn-limpiar-filtros">
              <XIcon size={14} /> Limpiar
            </button>
          )}
          <button className="btn-limpiar" onClick={() => fetchData(filtrosAplicados ? filtros : {})} data-testid="btn-refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="tendencia-kpis" data-testid="tendencia-kpis">
        <div className="tendencia-kpi">
          <div className="tendencia-kpi-icon" style={{ background: '#1565C0' }}><FileText size={22} /></div>
          <div className="tendencia-kpi-content">
            <h3 data-testid="kpi-total-casos">{resumen.total_casos || 0}</h3>
            <span>Casos MM</span>
          </div>
        </div>
        <div className="tendencia-kpi">
          <div className="tendencia-kpi-icon" style={{ background: '#E53935' }}><Globe size={22} /></div>
          <div className="tendencia-kpi-content">
            <h3 data-testid="kpi-redes">{resumen.redes_sociales || 0}</h3>
            <span>Redes Sociales</span>
          </div>
        </div>
        <div className="tendencia-kpi">
          <div className="tendencia-kpi-icon" style={{ background: '#FB8C00' }}><User size={22} /></div>
          <div className="tendencia-kpi-content">
            <h3 data-testid="kpi-autores">{resumen.autores_unicos || 0}</h3>
            <span>Autores</span>
          </div>
        </div>
        <div className="tendencia-kpi">
          <div className="tendencia-kpi-icon" style={{ background: '#8E24AA' }}><Tag size={22} /></div>
          <div className="tendencia-kpi-content">
            <h3 data-testid="kpi-tematicas">{resumen.tematicas || 0}</h3>
            <span>Tematicas</span>
          </div>
        </div>
        <div className="tendencia-kpi">
          <div className="tendencia-kpi-icon" style={{ background: '#00ACC1' }}><Users size={22} /></div>
          <div className="tendencia-kpi-content">
            <h3 data-testid="kpi-personas">{resumen.personas_mencionadas || 0}</h3>
            <span>Personas Mencionadas</span>
          </div>
        </div>
        <div className="tendencia-kpi">
          <div className="tendencia-kpi-icon" style={{ background: '#43A047' }}><Building2 size={22} /></div>
          <div className="tendencia-kpi-content">
            <h3 data-testid="kpi-instituciones">{resumen.instituciones || 0}</h3>
            <span>Instituciones</span>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="tendencia-timeline-section" data-testid="timeline-section">
        <h2><Activity size={18} /> Linea de Tiempo - Casos de Monitoreo de Medios</h2>
        {networkTimeline.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={networkTimeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="total" name="Total Casos" stroke="#1565C0" fill="#1565C0" fillOpacity={0.15} strokeWidth={2} />
              {redesList.map((red, i) => (
                <Area key={red} type="monotone" dataKey={red} name={red} stroke={RED_COLORS[red] || COLORS_CHART[i % COLORS_CHART.length]} fill={RED_COLORS[red] || COLORS_CHART[i % COLORS_CHART.length]} fillOpacity={0.08} strokeWidth={1.5} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="tendencia-empty"><Activity size={40} style={{ opacity: 0.3 }} /><p>Sin datos para la linea de tiempo</p></div>
        )}
      </div>

      {/* Charts */}
      <div className="tendencia-charts">
        {/* Red Social */}
        <div className="tendencia-chart-card" data-testid="chart-redes">
          <h2><Globe size={16} /> Casos por Red Social</h2>
          {(data?.por_red || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.por_red} dataKey="cantidad" nameKey="nombre" cx="50%" cy="50%" outerRadius={100} label={({ nombre, cantidad }) => `${nombre}: ${cantidad}`}>
                  {data.por_red.map((entry, i) => (<Cell key={i} fill={RED_COLORS[entry.nombre] || COLORS_CHART[i % COLORS_CHART.length]} />))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (<div className="tendencia-empty"><p>Sin datos</p></div>)}
        </div>

        {/* Tematica */}
        <div className="tendencia-chart-card" data-testid="chart-tematica">
          <h2><Tag size={16} /> Casos por Tematica</h2>
          {(data?.por_tematica || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.por_tematica} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="nombre" type="category" width={120} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cantidad" name="Casos" fill="#8E24AA" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="cantidad" position="right" style={{ fontSize: 11, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (<div className="tendencia-empty"><p>Sin datos</p></div>)}
        </div>

        {/* Autores */}
        <div className="tendencia-chart-card" data-testid="chart-autores">
          <h2><User size={16} /> Casos por Autor</h2>
          {(data?.por_autor || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.por_autor} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="nombre" type="category" width={130} tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cantidad" name="Casos" fill="#E53935" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="cantidad" position="right" style={{ fontSize: 11, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (<div className="tendencia-empty"><p>Sin datos</p></div>)}
        </div>

        {/* Personas Mencionadas */}
        <div className="tendencia-chart-card" data-testid="chart-personas">
          <h2><Users size={16} /> Personas Mencionadas</h2>
          {(data?.por_persona || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.por_persona} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="nombre" type="category" width={150} tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cantidad" name="Menciones" fill="#FB8C00" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="cantidad" position="right" style={{ fontSize: 11, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (<div className="tendencia-empty"><p>Sin datos</p></div>)}
        </div>

        {/* Instituciones */}
        <div className="tendencia-chart-card" data-testid="chart-instituciones">
          <h2><Building2 size={16} /> Casos por Institucion</h2>
          {(data?.por_institucion || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.por_institucion} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis dataKey="nombre" type="category" width={150} tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cantidad" name="Casos" fill="#00ACC1" radius={[0, 6, 6, 0]}>
                  <LabelList dataKey="cantidad" position="right" style={{ fontSize: 11, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (<div className="tendencia-empty"><p>Sin datos</p></div>)}
        </div>

        {/* Estado */}
        <div className="tendencia-chart-card" data-testid="chart-estados">
          <h2><AlertCircle size={16} /> Casos por Estado</h2>
          {(data?.por_estado || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.por_estado} dataKey="cantidad" nameKey="nombre" cx="50%" cy="50%" outerRadius={100} label={({ nombre, cantidad }) => `${nombre}: ${cantidad}`}>
                  {data.por_estado.map((entry, i) => (<Cell key={i} fill={COLORS_CHART[i % COLORS_CHART.length]} />))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (<div className="tendencia-empty"><p>Sin datos</p></div>)}
        </div>
      </div>

      {/* Tendencia por Red en el Tiempo */}
      {networkTimeline.length > 1 && redesList.length > 0 && (
        <div className="tendencia-timeline-section" data-testid="timeline-redes">
          <h2><Activity size={18} /> Tendencia por Red Social en el Tiempo</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={networkTimeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              {redesList.map((red, i) => (
                <Line key={red} type="monotone" dataKey={red} name={red} stroke={RED_COLORS[red] || COLORS_CHART[i % COLORS_CHART.length]} strokeWidth={2} dot={{ r: 4 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabla de Casos */}
      <div className="tendencia-table-section" data-testid="tabla-casos">
        <h2><FileText size={16} /> Detalle de Casos Monitoreo de Medios ({(data?.casos_detalle || []).length})</h2>
        <div className="tendencia-table-wrapper">
          {(data?.casos_detalle || []).length > 0 ? (
            <table className="tendencia-table">
              <thead>
                <tr>
                  <th>No. Gestion</th>
                  <th>Fecha</th>
                  <th>Red Social</th>
                  <th>Autor</th>
                  <th>Tematica</th>
                  <th>Personas</th>
                  <th>Institucion</th>
                  <th>Asesor CNC</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {(data?.casos_detalle || []).map((caso, i) => (
                  <tr key={`${caso.id}-${i}`}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{caso.nomenclatura}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{(caso.fecha || '').substring(0, 10)}</td>
                    <td>
                      <span className={`red-badge ${RED_COLORS[caso.red_social] ? caso.red_social : 'default'}`}>
                        {caso.red_social || '-'}
                      </span>
                    </td>
                    <td>{caso.autor || '-'}</td>
                    <td>{caso.tematica || '-'}</td>
                    <td>{caso.personas || '-'}</td>
                    <td>{caso.institucion || '-'}</td>
                    <td>{caso.asesor_cnc || '-'}</td>
                    <td>
                      <span className={`estado-badge ${(caso.estado || '').replace(/[\s/]/g, '-')}`}>
                        {caso.estado || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="tendencia-empty">
              <Search size={48} />
              <h3>Sin casos de Monitoreo de Medios</h3>
              <p>No hay casos que coincidan con los filtros aplicados</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TendenciaMonitoreo;
